import { CopyResourceDc, ServiceInfoDc, TableInfoDc } from "@evergis/api";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { dialogResourceSelector } from "../../../../../../ducks/dialogs/selectors";
import { getCopyJobInput } from "../../../../../../evergis/core/copy/getCopyJobInput";
import { useProjectPreview } from "../../../../../../hooks/preview/usePreview";
import { useCheckLimits } from "../../../../../../hooks/useCheckLimits";
import { projectInfosWithDeps } from "../../../../../../resources/projects";
import { getResourceName } from "../../../../../../utils/resourceName";
import { getMetaFromResource, useResourceMeta } from "../../../../../../hooks/useResourceMeta";
import { useAfterCopy } from "../../../hooks/useAfterCopy";
import { JobSource, useCopyJobNotifications } from "../../../hooks/useCopyJobNotifications";
import { useCopyResources } from "../../../hooks/useCopyResources";
import { useResourcesLimits } from "../../../hooks/useResourcesLimits";
import { useUpdateMetaEffect } from "../../../hooks/useUpdateMetaEffect";
import { useDefaultScaleRange } from "../../../../../../hooks/style/useDefaultScaleRange";
import { TableStructureState, TableStructureTable } from "./useTableStructureControls";
import { CopyResourceMeta } from "../../../types";

type CopyProjectMeta = Omit<CopyResourceMeta, "onApply"> & {
  deps: {
    layers: ServiceInfoDc[];
    tables: TableInfoDc[];
  };
  onApply: (tableStructure: TableStructureState) => void;
};

// eslint-disable-next-line max-lines-per-function
export const useCopyProjectJob = (filteredLayers?: ServiceInfoDc[], isCopySharedProject = false): CopyProjectMeta => {
  const currentResourceName: string = useSelector(dialogResourceSelector);
  const { data: projectInfo, loading: projectInfoLoading } = projectInfosWithDeps.use({ input: [currentResourceName] });
  const currentProject = projectInfo && projectInfo.projects[0];
  const deps = projectInfo && projectInfo.deps;
  const [previewUrl] = useProjectPreview(currentProject && currentProject.name);
  const [meta, updateMeta] = useResourceMeta(getMetaFromResource(currentProject, previewUrl));
  const { copyJob, copyData: jobResult, copyLoading: jobLoading, copyError: jobError } = useCopyResources();
  const [waiting, setWaiting] = useState(false);
  const notificationInfos = useCopyJobNotifications(JobSource.Projects, { alias: meta.alias, jobResult });
  const defaultScaleRange = useDefaultScaleRange();

  const { setInnerTasks } = useAfterCopy(
    notificationInfos,
    { jobResult, jobLoading, jobError },
    filteredLayers,
    isCopySharedProject,
  );

  const { limits } = useCheckLimits();
  const { isInitialTablesLimit } = useResourcesLimits(limits);

  useUpdateMetaEffect(currentProject, previewUrl, updateMeta);

  const onApply = useCallback(
    async (tableStructure: TableStructureState) => {
      if (!currentProject || !tableStructure) {
        return;
      }

      const { alias, description } = meta;
      const newResourceName: string = getResourceName();

      const tablesToCopy: TableInfoDc[] = isInitialTablesLimit ? [] : tableStructure.filter(item => item.selected);
      const layersToCopy: ServiceInfoDc[] = tableStructure.reduce((acc: ServiceInfoDc[], curr: TableStructureTable) => {
        return [...acc, ...curr.layers.filter(item => item.selected)];
      }, []);

      const [layerInput, tableInput, innerTasks] = getCopyJobInput({
        layers: layersToCopy,
        tables: tablesToCopy,
        copyTables: !!tablesToCopy.length,
        defaultScaleRange,
      });
      const copyWith: string[] | null = layerInput.length ? layerInput.map(item => item.copyName) : null;

      setWaiting(true);
      setInnerTasks(innerTasks);

      await copyJob({
        input: [
          {
            type: "project",
            name: currentResourceName,
            copyName: newResourceName,
            copyAlias: alias,
            copyDescription: description,
            copyWith: isCopySharedProject ? null : copyWith,
          } as CopyResourceDc,
          ...layerInput,
          ...tableInput,
        ],
      });
    },
    [
      currentProject,
      meta,
      isInitialTablesLimit,
      defaultScaleRange,
      setInnerTasks,
      copyJob,
      currentResourceName,
      isCopySharedProject,
    ],
  );

  return {
    deps,
    meta,
    updateMeta,
    onApply,
    waiting,
    loading: projectInfoLoading,
  };
};
